import gameSettings, { incrementPxToCoord, getIsGamePt } from '../constants/gameSettings';
import AbstractDomElement from '../../dom/views/abstractDomElement';
import gameObjectsSettings from '../constants/gameObjectsSettings/gameObjectsSettings';
import gameObjectTypes from '../constants/gameObjectTypes';
import { getRandomInt, getRandomIntInclusive } from '../helpers/mathMethods';

export default class GameObjectView {
    #currentRoadWidth;
    #currentRoadHeight;
    #isPt = false;
    #startPoint;

    #movementPoint;
    get movementPoint() {
        return this.#movementPoint;
    }

    gameObjectEl;

    #objectType;
    #additionalEls = [];

    defaultStyles = {
        transform: 'unset'
    };

    #hideClass = 'hide';

    constructor(objectType, options = {}) {
        if (!objectType) return;

        this.#objectType = objectType;
        this.#isPt = getIsGamePt();
        this.#startPoint = options.startPoint;

        this.createElements();
        this.setElementsPosition();
        this.setAdditionStyles();
        this.createAdditionalElements();
    }

    init() {
        if (!this.#objectType) return;

        this.setClasses();
    }

    updateGameSizes({ width, height }) {
        this.#currentRoadWidth = width * incrementPxToCoord;
        this.#currentRoadHeight = height * incrementPxToCoord;
        this.setElementsPosition();
    }

    createElements() {
        this.#movementPoint = new AbstractDomElement({ tag: 'div', classes: ['movement-point'] });
        this.gameObjectEl = new AbstractDomElement({ tag: 'div' });
    }

    createAdditionalElements() {
        const { additionalElements } = gameObjectsSettings[this.#objectType];

        if (!additionalElements) return;

        const { baseClasses, elements } = additionalElements;

        elements.forEach((element) => {
            const elToAdd = {
                id: element.id,
                neverHide: element.neverHide,
                instance: new AbstractDomElement({
                    tag: 'div', id: element.id, classes: [...baseClasses, ...element.classes || []]
                })
            };

            if (element.hidden) {
                elToAdd.instance.hide();
            }

            let parent = this.gameObjectEl.el;

            if (element.appendTo) {
                const elementToAddTo = this.#additionalEls.find((el) => el.id === element.appendTo);

                parent = elementToAddTo.instance.el;
            }

            elToAdd.instance.appendToParent(parent);

            this.#additionalEls.push(elToAdd);
        });
    }

    showAdditionalElement(id) {
        const element = this.#additionalEls.find((el) => el.id === id);

        if (!element) return;

        element.instance.show();
    }

    hideAdditionalElement(id) {
        const element = this.#additionalEls.find((el) => el.id === id);

        if (!element) return;

        element.instance.hide();
    }

    hideAllAdditionalElements() {
        this.#additionalEls.forEach((el) => {
            if (!el.neverHide) {
                el.instance.hide();
            }
        });
    }

    setElementsPosition() {
        if (!this.#startPoint) {
            if (this.#isPt && gameObjectsSettings[this.#objectType].startPointPt) {
                this.#startPoint = gameObjectsSettings[this.#objectType].startPointPt;
            } else {
                this.#startPoint = gameObjectsSettings[this.#objectType].startPointForView
                || gameObjectsSettings[this.#objectType].startPoint;
            }
        }

        let xMod = 100;

        // Костыль
        if (this.#isPt && this.#objectType === gameObjectTypes.MAIN_CHARACTER) {
            xMod = 335;
        }

        this.defaultStyles.left = `${50 + (this.#startPoint.x * xMod) / gameSettings.roadWidthPx}%`;
        this.defaultStyles.bottom = `${(this.#startPoint.y * 100) / gameSettings.roadHeightPx}%`;

        this.gameObjectEl.setStyles({
            transform: `scale(${this.#startPoint.scale})`
        });

        this.#movementPoint.setStyles(this.defaultStyles);

        // timeout для того, чтобы сначала установились стили, потом уже элемент добавился на страницу с новыми стилями
        setTimeout(() => {
            this.#movementPoint.appendToParent();
            this.gameObjectEl.appendToParent(this.#movementPoint.el);
        }, 0);
    }

    setAdditionStyles() {
        switch (this.#objectType) {
            case gameObjectTypes.DIVIDE_LINE:
                this.#movementPoint.setStyles({
                    zIndex: 2
                });

                break;
            case gameObjectTypes.MAIN_CHARACTER:
                this.#movementPoint.setStyles({
                    zIndex: 10002,
                    transition: 'transform 0.2s ease-out'
                });

                break;
            case gameObjectTypes.REGION_1_BUILDING:
            case gameObjectTypes.REGION_3_BUILDING:
            case gameObjectTypes.REGION_5_BUILDING:
                this.setZIndex5();

                break;
            case gameObjectTypes.LOOT_OBJECT:
            case gameObjectTypes.REGION_1_SIGN:
            case gameObjectTypes.REGION_2_SIGN:
            case gameObjectTypes.REGION_3_SIGN:
            case gameObjectTypes.REGION_4_SIGN:
            case gameObjectTypes.REGION_5_SIGN:
                this.setZIndex6();

                break;
            default:
                this.#movementPoint.setStyles({
                    zIndex: 3
                });

                break;
        }
    }

    setZIndex6() {
        this.#movementPoint.setStyles({
            zIndex: 6
        });
    }

    setZIndex5() {
        this.#movementPoint.setStyles({
            zIndex: 5
        });
    }

    setZIndexNum(index) {
        this.#movementPoint.setStyles({
            zIndex: index
        });
    }

    setClasses() {
        const objectSettings = gameObjectsSettings[this.#objectType];
        const objectClasses = [...objectSettings.objectClasses, 'game-object'];

        if (gameObjectsSettings[this.#objectType].objectClassOptions) {
            objectClasses.push(gameObjectsSettings[this.#objectType].objectClassOptions[
                getRandomInt(0, gameObjectsSettings[this.#objectType].objectClassOptions.length - 1)
            ]);
        }

        const { scaleRandomness, randomizeHorizontalReflection } = objectSettings;

        if (scaleRandomness) {
            const { min, max } = scaleRandomness;
            const randomScale = getRandomIntInclusive(min * 10, max * 10) / 10;

            this.gameObjectEl.setCssVariable('--random-scale-factor', randomScale);
        }

        if (randomizeHorizontalReflection) {
            const reflectionModifier = getRandomIntInclusive(0, 1) ? 1 : -1;

            this.gameObjectEl.setCssVariable('--x-reflection-modifier', reflectionModifier);
        }

        this.gameObjectEl.setClasses(objectClasses);
    }

    hide() {
        this.gameObjectEl.addClasses([this.#hideClass]);
    }

    show() {
        this.gameObjectEl.removeClasses([this.#hideClass]);
    }

    remove() {
        this.gameObjectEl.remove();
    }

    // x, y - относительно начального положения
    update(x, y, scale) {
        if (x !== undefined && y !== undefined) {
            const currentX = (x * this.#currentRoadHeight) / gameSettings.roadHeightMeter;
            const currentY = (y * this.#currentRoadHeight) / gameSettings.roadHeightMeter;

            // не заметила и оказывается добавила scale 2 раза...
            // TODO по хорошему переделать, чтоб scale был только у внутреннего объекта...
            // Хотя двойной scale даже добавляет объем.
            this.#movementPoint.setStyles({
                transform: `translate(${currentX / incrementPxToCoord}px, ${-1 * (currentY / incrementPxToCoord)}px) scale(${scale || 1})`
            });
        }

        if (scale !== undefined) {
            this.gameObjectEl.setStyles({
                transform: `scale(${scale})`
            });
        }
    }
}
